<script setup lang="ts">
// Mock of filming a video vacancy question by question, styled after the real question list.
import { useElementVisibility, useIntervalFn } from '@vueuse/core'
import { testimonials } from './landing-content'
import LandingVideo from './LandingVideo.vue'

const questions = [
  { title: 'О компании', hint: 'Кто вы и чем гордитесь', clip: 1 },
  { title: 'О вакансии', hint: 'Обязанности и ожидания', clip: 3 },
  { title: 'Условия и график', hint: 'Смены, оплата, бонусы', clip: 4 },
  { title: 'Команда', hint: 'Покажите зал и коллег', clip: 5 },
]

const root = useTemplateRef<HTMLElement>('root')
const visible = useElementVisibility(root)
const step = ref(1)
const recording = computed(() => step.value < questions.length ? step.value : -1)
const done = computed(() => step.value >= questions.length)

const { pause, resume } = useIntervalFn(() => {
  step.value = step.value >= questions.length + 1 ? 1 : step.value + 1
}, 1700, { immediate: false })
watch(visible, value => value ? resume() : pause())
</script>

<template>
  <div ref="root" class="flex size-full flex-col bg-background px-4 pb-6 pt-12 text-[15px]">
    <p class="text-[21px] font-semibold">Видеовакансия</p>
    <p class="text-sm text-muted-foreground">Бариста · Кофейня «Зерно»</p>

    <ul class="mt-4 flex flex-1 flex-col gap-2.5">
      <li
        v-for="(question, index) in questions"
        :key="question.title"
        class="flex min-h-[100px] items-center gap-3 rounded-2xl p-3 transition-colors duration-500"
        :class="index < step ? 'bg-muted' : 'border border-dashed bg-background'"
      >
        <span class="relative size-[76px] shrink-0 overflow-hidden rounded-xl bg-secondary">
          <LandingVideo v-if="index < step" :src="testimonials[question.clip]!.src" :autoplay="false" :frame="2" class="size-full object-cover" />
          <Icon v-else name="tabler:video-filled" class="absolute inset-0 m-auto size-7 text-primary" />
          <span v-if="index === recording" class="absolute inset-0 flex items-center justify-center bg-black/40">
            <span class="size-3 animate-pulse rounded-full bg-destructive" />
          </span>
        </span>
        <span class="min-w-0 flex-1">
          <span class="block font-semibold">{{ question.title }}</span>
          <span class="block text-sm text-muted-foreground">{{ question.hint }}</span>
          <span
            v-if="index < step && index !== recording"
            class="mt-1.5 inline-flex items-center gap-1 rounded-full bg-status-online/10 px-2 py-0.5 text-xs font-medium text-status-online"
          >
            <Icon name="tabler:circle-check-filled" class="size-3.5" />
            Ответ записан
          </span>
          <span v-else-if="index === recording" class="mt-1.5 inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
            Идёт запись…
          </span>
        </span>
      </li>
    </ul>

    <span
      class="mt-4 flex h-12 items-center justify-center rounded-full text-base font-medium transition-colors duration-500"
      :class="done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'"
    >
      {{ done ? 'Опубликовать' : `Записано ${Math.min(step, questions.length)} из ${questions.length}` }}
    </span>
  </div>
</template>
